import React, { useState, useEffect, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AuthContext, API } from '@/App';
import axios from 'axios';
import { toast } from 'sonner';
import { BookOpen, CheckCircle, ChevronRight, Loader, Lock, Sparkles, X, ArrowLeft, Plus, Trash2, Pencil } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import GuestModeBanner from '@/components/GuestModeBanner';
import { getLevel } from '@/utils/readingLevels';

const FolioStoryCard = ({ story, isAdmin, onEdit, onGenerateImage, isGenerating, isUnlocked, isGuest }) => {
    const [expanded, setExpanded] = useState(false);

    return (
        <div 
            className="sketch-border rounded-lg p-4 transition-all hover:shadow-md relative flex flex-col h-full cursor-pointer group/card" 
            style={{ background: 'var(--bg-secondary)' }}
            onClick={() => setExpanded(!expanded)}
        >
            {(story.thumbnail || (story.images && story.images.length > 0) || !isGuest) && (
                <div className="mb-3 w-full aspect-video rounded-md overflow-hidden bg-gray-100 dark:bg-gray-800 flex items-center justify-center relative group">
                    {story.thumbnail || (story.images && story.images.length > 0) ? (
                        <img 
                            src={`data:image/png;base64,${story.thumbnail || story.images[0]}`} 
                            alt={story.title} 
                            className="w-full h-full object-cover"
                        />
                    ) : (
                        <BookOpen className="text-gray-300 w-12 h-12" />
                    )}
                    
                    {isAdmin && (
                        <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity bg-white/80 dark:bg-black/50 rounded-lg p-1 backdrop-blur-sm">
                            <Button 
                                onClick={(e) => { e.stopPropagation(); onGenerateImage(story); }} 
                                variant="ghost" 
                                size="icon" 
                                className="h-8 w-8 hover:text-blue-600"
                                title="Generate Image"
                                disabled={isGenerating}
                            >
                                {isGenerating ? <Loader className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                            </Button>
                            <Button 
                                onClick={(e) => { e.stopPropagation(); onEdit(story); }} 
                                variant="ghost" 
                                size="icon" 
                                className="h-8 w-8 hover:text-purple-600"
                                title="Edit Story"
                            >
                                <Pencil className="h-4 w-4" />
                            </Button>
                        </div>
                    )}
                </div>
            )}
            <div className="flex justify-between items-start mb-1">
                <h4 className="font-bold text-md flex items-center" style={{ color: 'var(--text-primary)', fontFamily: 'Fredoka, sans-serif' }}>
                    <span className="mr-2" style={{ color: 'var(--accent-2)' }}>{story.order_in_series}.</span>
                    {story.title}
                </h4>
            </div>

            {isUnlocked && (
                <Link 
                    to={`/story/${story.id}`}
                    className="mb-3 inline-block w-full text-center py-1.5 rounded-md text-xs font-bold transition-colors"
                    style={{ background: 'var(--accent-3)', color: 'white' }}
                    onClick={(e) => e.stopPropagation()}
                >
                    READ STORY
                </Link>
            )}

            <div className="text-sm" style={{ color: 'var(--text-primary)' }}>
                {story.summary ? (
                    <p className={expanded ? "" : "line-clamp-3"}>{story.summary}</p>
                ) : (
                    <p className="text-xs italic opacity-60">Sign in to see story summary and illustrations</p>
                )}
                {(expanded || isGuest) && story.moral && (
                    <div className="mt-3 pt-2 border-t border-dashed border-gray-300 dark:border-gray-700">
                        <span className="text-xs font-bold uppercase" style={{ color: 'var(--accent-2)' }}>Moral:</span>
                        <p className="italic mt-0.5">{story.moral}</p>
                    </div>
                )}
            </div>
            {!expanded && (
                 <div className="mt-auto pt-1 text-[10px] text-center opacity-0 group-hover/card:opacity-50 transition-opacity uppercase tracking-wider" style={{ color: 'var(--text-secondary)' }}>
                    Click to view details
                </div>
            )}
        </div>
    );
};

const AssignmentModal = ({ isOpen, onClose, series, onAssign, isAssigning, token, platformTier }) => {
    const [children, setChildren] = useState([]);
    const [avatars, setAvatars] = useState([]);
    const [selectedChild, setSelectedChild] = useState('');
    const [selectedAvatar, setSelectedAvatar] = useState('');

    useEffect(() => {
        const fetchCharacters = async () => {
            if (isOpen) {
                try {
                    const [childrenRes, avatarsRes] = await Promise.all([
                        axios.get(`${API}/children/list/names`, { headers: { Authorization: `Bearer ${token}` } }),
                        axios.get(`${API}/avatars/list/names`, { headers: { Authorization: `Bearer ${token}` } })
                    ]);
                    setChildren(childrenRes.data);
                    setAvatars(avatarsRes.data);
                    if (childrenRes.data.length > 0) {
                        setSelectedChild(childrenRes.data[0].id);
                    }
                } catch (error) {
                    toast.error("Failed to load your children and avatars.");
                    console.error(error);
                }
            }
        };
        fetchCharacters();
    }, [isOpen]);

    if (!isOpen) return null;

    const handleAssignClick = () => {
        if (!selectedChild) {
            toast.error("Please select a child to personalize the series.");
            return;
        }
        onAssign(selectedChild, selectedAvatar, false);
    };

    const handleReadOnlyClick = () => {
        onAssign(null, null, true);
    };

    const canPersonalize = ['premium', 'family'].includes(platformTier);

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="rounded-xl shadow-2xl p-4 w-full max-w-sm relative" style={{ background: 'var(--bg-secondary)', border: '2px solid var(--sketch-border)' }}>
                <button onClick={onClose} className="absolute top-3 right-3 hover:opacity-70" style={{ color: 'var(--text-secondary)' }}>
                    <X size={24} />
                </button>
                <h2 className="text-xl font-bold mb-1" style={{ fontFamily: 'Fredoka, sans-serif', color: 'var(--accent-2)' }}>Assign Series: <span className="font-semibold" style={{ color: 'var(--text-primary)' }}>{series.name}</span></h2>
                <p className="mb-3 text-xs" style={{ color: 'var(--text-secondary)' }}>Unlocking a series consumes one Folio Access Pass.</p>

                <div className="space-y-3">
                    <div>
                        <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>
                            Personalize for Child (Recommended)
                        </label>
                        <div className="grid grid-cols-2 gap-2">
                            {children.map(child => (
                                <button
                                    key={child.id}
                                    onClick={() => setSelectedChild(child.id)}
                                    className="py-1.5 px-2 rounded-md border text-xs transition-all"
                                    style={{
                                        background: selectedChild === child.id ? 'var(--accent-2)' : 'var(--bg-primary)',
                                        color: selectedChild === child.id ? 'white' : 'var(--text-primary)',
                                        borderColor: selectedChild === child.id ? 'var(--accent-2)' : 'var(--sketch-border)'
                                    }}
                                >
                                    {child.name}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div>
                        <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>
                            Include Avatar (Optional)
                        </label>
                        <div className="grid grid-cols-2 gap-2">
                            <button
                                onClick={() => setSelectedAvatar('')}
                                className="py-1.5 px-2 rounded-md border text-xs transition-all"
                                style={{
                                    background: selectedAvatar === '' ? 'var(--accent-2)' : 'var(--bg-primary)',
                                    color: selectedAvatar === '' ? 'white' : 'var(--text-primary)',
                                    borderColor: selectedAvatar === '' ? 'var(--accent-2)' : 'var(--sketch-border)'
                                }}
                            >
                                No Avatar
                            </button>
                            {avatars.map(avatar => (
                                <button
                                    key={avatar.id}
                                    onClick={() => setSelectedAvatar(avatar.id)}
                                    className="py-1.5 px-2 rounded-md border text-xs transition-all"
                                    style={{
                                        background: selectedAvatar === avatar.id ? 'var(--accent-2)' : 'var(--bg-primary)',
                                        color: selectedAvatar === avatar.id ? 'white' : 'var(--text-primary)',
                                        borderColor: selectedAvatar === avatar.id ? 'var(--accent-2)' : 'var(--sketch-border)'
                                    }}
                                >
                                    {avatar.name}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="mt-6 flex flex-col gap-2">
                    <Button
                        onClick={handleAssignClick}
                        disabled={isAssigning || !canPersonalize}
                        className="pill-button w-full"
                        size="sm"
                        style={{ background: canPersonalize ? 'var(--accent-2)' : '#94a3b8', color: 'white' }}
                    >
                        {isAssigning && <Loader size={16} className="animate-spin mr-2" />}
                        {!canPersonalize && <Lock size={14} className="mr-2" />}
                        {isAssigning ? 'Processing...' : 'Option 1: Personalize & Add to Library'}
                    </Button>
                    
                    {!canPersonalize && (
                        <p className="text-[10px] text-center text-amber-600 font-medium">
                            Personalization requires Premium or Family tier.
                        </p>
                    )}

                    <Button
                        onClick={handleReadOnlyClick}
                        disabled={isAssigning}
                        variant="outline"
                        className="pill-button w-full"
                        style={{ background: 'var(--accent-2)', color: 'white' }}
                        size="sm"
                    >
                        Option 2: Read with Original Characters
                    </Button>
                    <Button
                        onClick={onClose}
                        disabled={isAssigning}
                        variant="ghost"
                        size="sm"
                        className="rounded-full"
                    >
                        Cancel
                    </Button>
                </div>
            </div>
        </div>
    );
};

const FolioStoryModal = ({ isOpen, onClose, token, onSuccess, storyToEdit, existingSeries }) => {
    const [formData, setFormData] = useState({
        series: '',
        title: '',
        order_in_series: 1,
        reading_level: 'simple',
        age_group: '5-6',
        topic: '',
        summary: '',
        content: '',
        moral: '',
        vocabulary: [],
        phonics: [],
        parent_prompts: [],
        child_name_placeholder: 'Bernice',
        avatar_name_placeholder: 'MilkyWay',
        gender_placeholder: 'female'
    });
    const [loading, setLoading] = useState(false);

    // Get selected series definition to display placeholders and lock attributes
    const selectedSeriesDef = existingSeries.find(s => s.name === formData.series);

    // When series changes, update age/level to match series definition if available
    // Only apply defaults for NEW stories (not edits) to prevent overwriting explicit story values
    useEffect(() => {
        if (selectedSeriesDef && !storyToEdit) {
            setFormData(prev => ({
                ...prev,
                age_group: selectedSeriesDef.age_group || prev.age_group,
                reading_level: selectedSeriesDef.reading_level || prev.reading_level
            }));
        }
    }, [formData.series, selectedSeriesDef, storyToEdit]);

    useEffect(() => {
        if (isOpen) {
            if (storyToEdit) {
                setFormData({
                    series: storyToEdit.series || '',
                    title: storyToEdit.title || '',
                    order_in_series: storyToEdit.order_in_series || 1,
                    reading_level: storyToEdit.reading_level || 'simple',
                    age_group: storyToEdit.age_group || '5-6',
                    topic: storyToEdit.topic || '',
                    summary: storyToEdit.summary || '',
                    content: storyToEdit.content || '',
                    moral: storyToEdit.moral || '',
                    vocabulary: storyToEdit.vocabulary || [],
                    phonics: storyToEdit.phonics || [],
                    parent_prompts: storyToEdit.parent_prompts || [],
                    child_name_placeholder: storyToEdit.child_name_placeholder || 'Bernice',
                    avatar_name_placeholder: storyToEdit.avatar_name_placeholder || 'MilkyWay',
                    gender_placeholder: storyToEdit.gender_placeholder || 'female'
                });
            } else {
                setFormData({
                    series: '',
                    title: '',
                    order_in_series: 1,
                    reading_level: 'simple',
                    age_group: '5-6',
                    topic: '',
                    summary: '',
                    content: '',
                    moral: '',
                    vocabulary: [],
                    phonics: [],
                    parent_prompts: [],
                    child_name_placeholder: 'Bernice',
                    avatar_name_placeholder: 'MilkyWay',
                    gender_placeholder: 'female'
                });
            }
        }
    }, [isOpen, storyToEdit]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: name === 'order_in_series' ? parseInt(value) || 0 : value
        }));
    };

    const handleVocabChange = (index, field, value) => {
        const newVocab = [...formData.vocabulary];
        newVocab[index] = { ...newVocab[index], [field]: value };
        setFormData({ ...formData, vocabulary: newVocab });
    };
    const addVocab = () => setFormData({ ...formData, vocabulary: [...formData.vocabulary, { word: '', definition: '' }] });
    const removeVocab = (index) => setFormData({ ...formData, vocabulary: formData.vocabulary.filter((_, i) => i !== index) });

    const handlePhonicsChange = (index, value) => {
        const newPhonics = [...formData.phonics];
        newPhonics[index] = value;
        setFormData({ ...formData, phonics: newPhonics });
    };
    const addPhonics = () => setFormData({ ...formData, phonics: [...formData.phonics, ''] });
    const removePhonics = (index) => setFormData({ ...formData, phonics: formData.phonics.filter((_, i) => i !== index) });

    const handlePromptChange = (index, value) => {
        const newPrompts = [...formData.parent_prompts];
        newPrompts[index] = value;
        setFormData({ ...formData, parent_prompts: newPrompts });
    };
    const addPrompt = () => setFormData({ ...formData, parent_prompts: [...formData.parent_prompts, ''] });
    const removePrompt = (index) => setFormData({ ...formData, parent_prompts: formData.parent_prompts.filter((_, i) => i !== index) });

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            if (storyToEdit) {
                await axios.patch(`${API}/admin/stories/${storyToEdit.id}/folio-settings`, formData, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                toast.success('Folio story updated successfully!');
            } else {
                await axios.post(`${API}/admin/folio/stories`, formData, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                toast.success('Folio story created successfully!');
            }
            onSuccess();
            onClose();
            // Reset form
            setFormData({
                series: '',
                title: '',
                order_in_series: 1,
                reading_level: 'simple',
                age_group: '5-6',
                topic: '',
                summary: '',
                content: '',
                moral: '',
                vocabulary: [],
                phonics: [],
                parent_prompts: []
            });
        } catch (error) {
            console.error(error);
            toast.error('Failed to create story');
        } finally {
            setLoading(false);
        }
    };

    const childPlaceholder = formData.child_name_placeholder;
    const avatarPlaceholder = formData.avatar_name_placeholder;
    const genderPlaceholder = formData.gender_placeholder;

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
            <div className="rounded-xl shadow-2xl p-4 w-full max-w-4xl relative my-4" style={{ background: 'var(--bg-secondary)', border: '2px solid var(--sketch-border)' }}>
                <button onClick={onClose} className="absolute top-3 right-3 hover:opacity-70" style={{ color: 'var(--text-secondary)' }}>
                    <X size={24} />
                </button>
                <h2 className="text-xl font-bold mb-3" style={{ fontFamily: 'Fredoka, sans-serif', color: 'var(--accent-2)' }}>{storyToEdit ? 'Edit Folio Story' : 'Add New Folio Story'}</h2>
                
                <form onSubmit={handleSubmit} className="space-y-2">
                    <div className="grid grid-cols-3 gap-3">
                        <div>
                            <Label>Series Name</Label>
                            {existingSeries.length > 0 ? (
                                <select 
                                    name="series" 
                                    value={formData.series} 
                                    onChange={handleChange}
                                    className="w-full p-1 border rounded-md h-8 text-sm"
                                    style={{ background: 'var(--bg-primary)', borderColor: 'var(--sketch-border)' }}
                                    required
                                >
                                    <option value="">Select a Series</option>
                                    {existingSeries.map(s => <option key={s.name} value={s.name}>{s.name}</option>)}
                                </select>
                            ) : (
                                <Input name="series" value={formData.series} onChange={handleChange} required placeholder="Create a series first" className="h-8 text-sm" />
                            )}
                        </div>
                        <div>
                            <Label>Story Title</Label>
                            <Input name="title" value={formData.title} onChange={handleChange} required placeholder="e.g. The Whispering Tree" className="h-8 text-sm" />
                        </div>
                        <div>
                            <Label>Order in Series</Label>
                            <Input type="number" name="order_in_series" value={formData.order_in_series} onChange={handleChange} required className="h-8 text-sm" />
                        </div>
                    </div>

                    <div className="grid grid-cols-3 gap-3">
                        <div>
                            <Label>Age Group</Label>
                            <select 
                                name="age_group" 
                                value={formData.age_group} 
                                onChange={handleChange}
                                disabled={!!selectedSeriesDef}
                                className="w-full p-1 border rounded-md h-8 text-sm"
                                style={{ background: 'var(--bg-primary)', borderColor: 'var(--sketch-border)', opacity: selectedSeriesDef ? 0.7 : 1 }}
                            >
                                <option value="3-4">3-4 years</option>
                                <option value="5-6">5-6 years</option>
                                <option value="7-8">7-8 years</option>
                                <option value="9-11">9-11 years</option>
                            </select>
                        </div>
                        <div>
                            <Label>Reading Level</Label>
                            <select 
                                name="reading_level" 
                                value={formData.reading_level} 
                                onChange={handleChange}
                                disabled={!!selectedSeriesDef}
                                className="w-full p-1 border rounded-md h-8 text-sm"
                                style={{ background: 'var(--bg-primary)', borderColor: 'var(--sketch-border)', opacity: selectedSeriesDef ? 0.7 : 1 }}
                            >
                                <option value="entry">Entry</option>
                                <option value="simple">Simple</option>
                                <option value="developing">Developing</option>
                                <option value="fluent">Fluent</option>
                            </select>
                        </div>
                        <div>
                            <Label>Topic</Label>
                            <Input name="topic" value={formData.topic} onChange={handleChange} required placeholder="e.g. Nature" className="h-8 text-sm" />
                        </div>
                    </div>

                    <div className="grid grid-cols-3 gap-3">
                        <div>
                            <Label>Main Character Placeholder</Label>
                            <Input name="child_name_placeholder" value={formData.child_name_placeholder} onChange={handleChange} placeholder="Bernice" className="h-8 text-sm" />
                        </div>
                        <div>
                            <Label>Avatar Placeholder</Label>
                            <Input name="avatar_name_placeholder" value={formData.avatar_name_placeholder} onChange={handleChange} placeholder="MilkyWay" className="h-8 text-sm" />
                        </div>
                        <div>
                            <Label>Gender</Label>
                            <select name="gender_placeholder" value={formData.gender_placeholder} onChange={handleChange} className="w-full p-1 border rounded-md h-8 text-sm" style={{ background: 'var(--bg-primary)', borderColor: 'var(--sketch-border)' }}>
                                <option value="female">Female (She/Her)</option>
                                <option value="male">Male (He/Him)</option>
                                <option value="neutral">Neutral (They/Them)</option>
                            </select>
                        </div>
                    </div>

                    <div>
                        <Label>Summary</Label>
                        <Input name="summary" value={formData.summary} onChange={handleChange} required placeholder="One line summary" className="h-8 text-sm" />
                    </div>

                    <div>
                        <Label>Content</Label>
                        <Textarea name="content" value={formData.content} onChange={handleChange} required rows={5} className="text-sm" />
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label>Moral</Label>
                            <Input name="moral" value={formData.moral} onChange={handleChange} className="h-8 text-sm" />
                        </div>
                        <div>
                            <div className="flex items-center justify-between">
                            <Label>Discussion Questions</Label>
                            <Button type="button" onClick={addPrompt} variant="ghost" size="sm" className="h-6 w-6 p-0"><Plus className="h-4 w-4" /></Button>
                        </div>
                        {formData.parent_prompts.map((prompt, index) => (
                            <div key={index} className="flex gap-1 mb-1">
                                <Input value={prompt} onChange={(e) => handlePromptChange(index, e.target.value)} placeholder="Question" className="h-8 text-xs" />
                                <Button type="button" onClick={() => removePrompt(index)} variant="ghost" size="icon" className="h-8 w-8 shrink-0"><Trash2 className="h-3 w-3 text-red-500" /></Button>
                            </div>
                        ))}
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4 border-t pt-2">
                        <div>
                            <div className="flex items-center justify-between mb-1">
                            <Label>Vocabulary</Label>
                            <Button type="button" onClick={addVocab} variant="ghost" size="sm" className="h-6 w-6 p-0"><Plus className="h-4 w-4" /></Button>
                        </div>
                        {formData.vocabulary.map((item, index) => (
                            <div key={index} className="flex gap-1 mb-1">
                                <Input value={item.word} onChange={(e) => handleVocabChange(index, 'word', e.target.value)} placeholder="Word" className="h-8 text-xs w-1/3" />
                                <Input value={item.definition} onChange={(e) => handleVocabChange(index, 'definition', e.target.value)} placeholder="Definition" className="h-8 text-xs w-2/3" />
                                <Button type="button" onClick={() => removeVocab(index)} variant="ghost" size="icon" className="h-8 w-8 shrink-0"><Trash2 className="h-3 w-3 text-red-500" /></Button>
                            </div>
                        ))}
                    </div>

                    <div>
                        <div className="flex items-center justify-between mb-1">
                            <Label>Phonics</Label>
                            <Button type="button" onClick={addPhonics} variant="ghost" size="sm" className="h-6 w-6 p-0"><Plus className="h-4 w-4" /></Button>
                        </div>
                        {formData.phonics.map((item, index) => (
                            <div key={index} className="flex gap-1 mb-1">
                                <Input value={item} onChange={(e) => handlePhonicsChange(index, e.target.value)} placeholder="e.g. Cat: Short 'a' sound" className="h-8 text-xs" />
                                <Button type="button" onClick={() => removePhonics(index)} variant="ghost" size="icon" className="h-8 w-8 shrink-0"><Trash2 className="h-3 w-3 text-red-500" /></Button>
                            </div>
                        ))}
                    </div>
                    </div>

                    <div className="flex justify-end gap-3 mt-2 pt-2 border-t">
                        <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
                        <Button type="submit" disabled={loading} className="pill-button" style={{ background: 'var(--accent-2)', color: 'white' }}>
                            {loading ? 'Saving...' : (storyToEdit ? 'Update Story' : 'Create Story')}
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    );
};

const SeriesEditorModal = ({ isOpen, onClose, token, onSuccess, seriesToEdit }) => {
    const [formData, setFormData] = useState({
        name: '',
        age_group: '5-6',
        reading_level: 'simple',
        keywords: '', // Comma separated for input
    });
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (isOpen) {
            if (seriesToEdit) {
                setFormData({
                    name: seriesToEdit.name,
                    age_group: seriesToEdit.age_group || '5-6',
                    reading_level: seriesToEdit.reading_level || 'simple',
                    keywords: (seriesToEdit.keywords || []).join(', ')
                });
            } else {
                setFormData({
                    name: '',
                    age_group: '5-6',
                    reading_level: 'simple',
                    keywords: ''
                });
            }
        }
    }, [isOpen, seriesToEdit]);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const payload = {
                ...formData,
                keywords: formData.keywords.split(',').map(k => k.trim()).filter(k => k)
            };
            
            await axios.post(`${API}/admin/folio/series-definitions`, payload, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            toast.success('Series definition saved successfully!');
            onSuccess();
            onClose();
        } catch (error) {
            console.error(error);
            toast.error('Failed to save series definition');
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="rounded-xl shadow-2xl p-5 w-full max-w-2xl relative" style={{ background: 'var(--bg-secondary)', border: '2px solid var(--sketch-border)' }}>
                <button onClick={onClose} className="absolute top-3 right-3 hover:opacity-70" style={{ color: 'var(--text-secondary)' }}>
                    <X size={24} />
                </button>
                <h2 className="text-xl font-bold mb-4" style={{ fontFamily: 'Fredoka, sans-serif', color: 'var(--accent-2)' }}>{seriesToEdit ? 'Edit Series' : 'New Series'}</h2>
                
                <form onSubmit={handleSubmit} className="space-y-3">
                    <div>
                        <Label>Series Name</Label>
                        <Input name="name" value={formData.name} onChange={handleChange} required disabled={!!seriesToEdit} placeholder="e.g. The Magic Forest" className="h-9" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label>Age Group</Label>
                            <select name="age_group" value={formData.age_group} onChange={handleChange} className="w-full p-2 border rounded-md h-9 text-sm" style={{ background: 'var(--bg-primary)' }}>
                                <option value="3-4">3-4 years</option>
                                <option value="5-6">5-6 years</option>
                                <option value="7-8">7-8 years</option>
                                <option value="9-11">9-11 years</option>
                            </select>
                        </div>
                        <div>
                            <Label>Reading Level</Label>
                            <select name="reading_level" value={formData.reading_level} onChange={handleChange} className="w-full p-2 border rounded-md h-9 text-sm" style={{ background: 'var(--bg-primary)' }}>
                                <option value="entry">Entry</option>
                                <option value="simple">Simple</option>
                                <option value="developing">Developing</option>
                                <option value="fluent">Fluent</option>
                            </select>
                        </div>
                    </div>
                    <div>
                        <Label>Keywords (comma separated)</Label>
                        <Input name="keywords" value={formData.keywords} onChange={handleChange} placeholder="e.g. Nature, Magic, Friendship" className="h-9" />
                    </div>

                    <div className="flex justify-end gap-3 mt-6 pt-3 border-t">
                        <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
                        <Button type="submit" disabled={loading} className="pill-button" style={{ background: 'var(--accent-2)', color: 'white' }}>
                            {loading ? 'Saving...' : 'Save Series'}
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    );
};

const FolioPage = () => {
    const navigate = useNavigate();
    const { user, refreshUser, nightMode, token } = useContext(AuthContext);
    const [seriesList, setSeriesList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [usage, setUsage] = useState(null);
    const [selectedSeries, setSelectedSeries] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isAssigning, setIsAssigning] = useState(false);
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [editingStory, setEditingStory] = useState(null);
    const [selectedSeriesFilter, setSelectedSeriesFilter] = useState([]);
    const [selectedAgeFilter, setSelectedAgeFilter] = useState([]);
    const [selectedKeywordFilter, setSelectedKeywordFilter] = useState([]);
    const [allKeywords, setAllKeywords] = useState([]);
    const [isSeriesEditorOpen, setIsSeriesEditorOpen] = useState(false);
    const [editingSeries, setEditingSeries] = useState(null);
    const [regeneratingIds, setRegeneratingIds] = useState([]);

    const isAdmin = user?.role === 'admin';
    const isGuestMode = user?.id === 'guest-user';
    const hasFolioPass = (usage?.folio_balance ?? user?.folio_balance ?? 0) > 0;
    const previouslySelectedSeries = user?.folio_selections?.map(s => s.series_name) || [];
    
    const platformTier = ['starter', 'folio', 'guest'].includes(user?.subscription_tier) ? 'free' : (user?.subscription_tier || 'free');

    const canSelectSeries = (seriesName) => {
        if (isAdmin) return true;
        if (previouslySelectedSeries.includes(seriesName)) return true;
        return hasFolioPass;
    };

    const fetchFolioData = async () => {
        try {
            setLoading(true);
            const isGuest = !token || token === 'guest-token';
            
            const promises = [
                axios.get(`${API}/folio/series`, { headers: { Authorization: `Bearer ${token}` } })
            ];
            
            if (!isGuest) {
                promises.push(axios.get(`${API}/auth/usage`, { headers: { Authorization: `Bearer ${token}` } }));
            }

            const results = await Promise.all(promises);
            
            setSeriesList(results[0].data.series_list);
            setAllKeywords(results[0].data.all_keywords || []);
            if (results[1]) setUsage(results[1].data);
        } catch (error) {
            toast.error('Failed to load Folio series.');
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchFolioData();
    }, [token]);

    const handleSelectSeries = (series) => {
        setSelectedSeries(series);
        setIsModalOpen(true);
    };

    const handleCreateStory = () => {
        setEditingStory(null);
        setIsCreateModalOpen(true);
    };

    const handleAddSeries = () => {
        setEditingSeries(null);
        setIsSeriesEditorOpen(true);
    };

    const handleEditSeries = (series) => {
        setEditingSeries(series);
        setIsSeriesEditorOpen(true);
    };

    const handleEditStory = (story) => {
        setEditingStory(story);
        setIsCreateModalOpen(true);
    };

    const handleAssign = async (childId, avatarId, readOnly) => {
        setIsAssigning(true);
        try {
            const payload = {
                series_name: selectedSeries.name,
                child_id: childId,
                avatar_id: avatarId || null,
                read_only_mode: readOnly
            };
            const response = await axios.post(`${API}/folio/assign-series`, payload, { headers: { Authorization: `Bearer ${token}` } });
            
            if (!readOnly && response.data.redirect_params) {
                const { child, avatar } = response.data.redirect_params;
                const query = new URLSearchParams({ child });
                if (avatar) query.append('avatar', avatar);
                query.append('is_folio', 'true');
                
                toast.success("Personalizing your stories... This may take a moment to appear in your library.");
                setTimeout(() => navigate(`/library?${query.toString()}`), 2000);
            } else {
                toast.success(response.data.message, { duration: 5000 });
                await refreshUser(); // Refresh user data to update selection status
            }

            await fetchFolioData(); // Refresh local usage state to sync balance
            setIsModalOpen(false);
            setSelectedSeries(null);
        } catch (error) {
            toast.error(error.response?.data?.detail || "Failed to assign series.");
            console.error(error);
        } finally {
            setIsAssigning(false);
        }
    };

    const handleGenerateImage = async (story) => {
        setRegeneratingIds(prev => [...prev, story.id]);
        try {
            await axios.post(`${API}/stories/${story.id}/regenerate-image`, {}, { headers: { Authorization: `Bearer ${token}` } });
            toast.success("Image generated successfully!");
            fetchFolioData();
        } catch (error) {
            toast.error("Failed to generate image.");
            console.error(error);
        } finally {
            setRegeneratingIds(prev => prev.filter(id => id !== story.id));
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-screen">
                <Loader size={48} className="animate-spin text-purple-600" />
            </div>
        );
    }

    const toggleSeriesFilter = (seriesName) => {
        setSelectedSeriesFilter(prev =>
            prev.includes(seriesName)
                ? prev.filter(s => s !== seriesName)
                : [...prev, seriesName]
        );
    };

    const toggleFilter = (filterState, setFilterState, value) => {
        setFilterState(prev =>
            prev.includes(value) ? prev.filter(i => i !== value) : [...prev, value]
        );
    };

    // Filter logic
    const allSeriesNames = [...new Set(seriesList.map(s => s.name))].sort();
    const allAges = [...new Set(seriesList.map(s => s.age_group))].sort();

    const filteredSeries = seriesList.filter(series => {
        const matchesSeries = selectedSeriesFilter.length === 0 || selectedSeriesFilter.includes(series.name);
        const matchesAge = selectedAgeFilter.length === 0 || selectedAgeFilter.includes(series.age_group);
        const matchesKeyword = selectedKeywordFilter.length === 0 || 
            (series.keywords && selectedKeywordFilter.every(k => series.keywords.includes(k)));
        
        return matchesSeries && matchesAge && matchesKeyword;
    });

    return (
        <div className={`min-h-screen ${nightMode ? 'night-gradient' : 'playful-gradient'} page-enter`}>
            {isGuestMode && <GuestModeBanner />}
            <div className="container mx-auto px-4 sm:px-6 py-6 sm:py-8">
                {/* Header */}
                <div className="flex items-center gap-4 mb-6">
                    <Button
                        onClick={() => navigate('/dashboard')}
                        variant="outline"
                        className="rounded-full flex-shrink-0 w-20"
                        style={{
                            background: nightMode ? 'var(--bg-secondary)' : 'white',
                            color: nightMode ? 'var(--text-primary)' : 'inherit',
                            borderColor: nightMode ? 'var(--sketch-border)' : 'inherit'
                        }}
                    >
                        <ArrowLeft className="w-5 h-5" />
                    </Button>
                    <h1 className="text-xl sm:text-2xl font-bold" style={{ fontFamily: 'Fredoka, sans-serif', color: 'var(--text-primary)' }}>
                        Folio Story Series
                    </h1>
                    
                    {user?.role === 'admin' && (
                        <div className="ml-auto flex gap-2">
                            <Button
                                onClick={handleAddSeries}
                                className="pill-button"
                                variant="outline"
                            >
                                <Plus className="w-4 h-4 mr-2" />
                                Add Series
                            </Button>
                            <Button
                                onClick={handleCreateStory}
                                className="pill-button"
                                style={{ background: 'var(--accent-2)', color: 'white' }}
                            >
                                <Plus className="w-4 h-4 mr-2" />
                                Add Story
                            </Button>
                        </div>
                    )}
                </div>

                
                <div className="text-center mb-4">
                    <p className="text-lg" style={{ color: 'var(--text-secondary)' }}>
                        Browse our curated collection of stories on key topics. Use a Folio Access Pass to unlock a series to personalize and add to your library, or simply read within the folio.
                    </p>
                </div>

                {!hasFolioPass && !isAdmin && previouslySelectedSeries.length === 0 && (
                    <div className="border-l-4 p-4 rounded-md mb-8" style={{ background: 'var(--accent-1)', borderColor: 'var(--accent-2)', color: 'white' }} role="alert">
                        <p className="font-bold text-lg">Unlock Folio Stories!</p>
                        <p>Purchase a <Link to="/subscription" className="underline font-bold hover:text-gray-200">Folio Access Pass</Link> to unlock a series and add it to your child's library.</p>
                    </div>
                )}

                {/* Filters */}
                <div className="mb-6 space-y-2">
                    <div className="flex flex-wrap gap-4">
                        <div className="flex flex-wrap items-center gap-1 mt-1">
                            <span className="text-xs font-medium mr-1" style={{ color: 'var(--text-secondary)' }}>Filter by Series:</span>
                            {allSeriesNames.map((seriesName) => (
                                <button
                                    key={seriesName}
                                    type="button"
                                    onClick={() => toggleSeriesFilter(seriesName)}
                                    className="px-2 py-0.5 rounded-full text-xs transition-colors border"
                                    style={{
                                        background: selectedSeriesFilter.includes(seriesName) ? 'var(--accent-1)' : (nightMode ? '#374151' : '#f3f4f6'),
                                        color: selectedSeriesFilter.includes(seriesName) ? 'white' : 'var(--text-primary)',
                                        borderColor: selectedSeriesFilter.includes(seriesName) ? 'var(--accent-1)' : (nightMode ? '#4b5563' : '#e5e7eb')
                                    }}
                                >
                                    {seriesName}
                                </button>
                            ))}
                        </div>
                        
                        <div className="flex flex-wrap items-center gap-1 mt-1">
                            <span className="text-xs font-medium mr-1" style={{ color: 'var(--text-secondary)' }}>By Age:</span>
                            {allAges.map((age) => (
                                <button
                                    key={age}
                                    onClick={() => toggleFilter(selectedAgeFilter, setSelectedAgeFilter, age)}
                                    className="px-2 py-0.5 rounded-full text-xs transition-colors border"
                                    style={{
                                        background: selectedAgeFilter.includes(age) ? 'var(--accent-3)' : (nightMode ? '#374151' : '#f3f4f6'),
                                        color: selectedAgeFilter.includes(age) ? 'white' : 'var(--text-primary)',
                                        borderColor: selectedAgeFilter.includes(age) ? 'var(--accent-3)' : (nightMode ? '#4b5563' : '#e5e7eb')
                                    }}
                                >
                                    {age}
                                </button>
                            ))}
                        </div>

                        {allKeywords.length > 0 && (
                            <div className="flex flex-wrap items-center gap-1 mt-1">
                                <span className="text-xs font-medium mr-1" style={{ color: 'var(--text-secondary)' }}>By Keyword:</span>
                                {allKeywords.map((kw) => (
                                    <button
                                        key={kw}
                                        onClick={() => toggleFilter(selectedKeywordFilter, setSelectedKeywordFilter, kw)}
                                        className="px-2 py-0.5 rounded-full text-xs transition-colors border"
                                        style={{
                                            background: selectedKeywordFilter.includes(kw) ? 'var(--accent-4)' : (nightMode ? '#374151' : '#f3f4f6'),
                                            color: selectedKeywordFilter.includes(kw) ? 'white' : 'var(--text-primary)',
                                            borderColor: selectedKeywordFilter.includes(kw) ? 'var(--accent-4)' : (nightMode ? '#4b5563' : '#e5e7eb')
                                        }}
                                    >
                                        {kw}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                    {(selectedSeriesFilter.length > 0 || selectedAgeFilter.length > 0 || selectedKeywordFilter.length > 0) && (
                        <button 
                            onClick={() => {
                                setSelectedSeriesFilter([]);
                                setSelectedAgeFilter([]);
                                setSelectedKeywordFilter([]);
                            }} 
                            className="flex items-center gap-1 px-2 py-1 text-xs" 
                            style={{ color: 'var(--accent-2)' }}
                        >
                            <X className="w-3 h-3" />
                            Clear All Filters
                        </button>
                    )}
                </div>

                <div className="space-y-12">
                    {filteredSeries.map((series) => {
                        const isAlreadyInLibrary = previouslySelectedSeries.includes(series.name);
                        const canCurrentlySelect = canSelectSeries(series.name);
                        
                        const storiesToDisplay = series.stories;

                        return (
                            <div key={series.name} className="story-card p-6" style={{ transform: 'none', transition: 'none' }}>
                                <div className="flex flex-col sm:flex-row justify-between sm:items-center mb-4">
                                    <div>
                                        <div className="flex items-center gap-3">
                                            <h2 className="text-2xl font-bold" style={{ fontFamily: 'Fredoka, sans-serif', color: 'var(--text-primary)' }}>{series.name}</h2>
                                            {isAdmin && (
                                                <Button 
                                                    onClick={() => handleEditSeries(series)}
                                                    variant="ghost" 
                                                    size="icon" 
                                                    className="h-6 w-6 text-gray-400 hover:text-blue-500"
                                                >
                                                    <Pencil size={14} />
                                                </Button>
                                            )}
                                        </div>
                                        {/* Series Metadata Compact Display */}
                                        <div className="flex flex-wrap items-center gap-2 mt-1 text-xs">
                                            <span className="px-2 py-0.5 rounded bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-100">
                                                Age: {series.age_group}
                                            </span>
                                            <span className="px-2 py-0.5 rounded bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-100 uppercase">
                                                {getLevel(series.reading_level)}
                                            </span>
                                            {series.keywords && series.keywords.map(kw => (
                                                <span key={kw} className="px-2 py-0.5 rounded bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300">
                                                    #{kw}
                                                </span>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-2">
                                        {isAlreadyInLibrary ? (
                                            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                                                <CheckCircle size={16} className="mr-1.5" />
                                                Available to you
                                            </span>
                                        ) : null}

                                        {canCurrentlySelect ? (
                                            <Button
                                                onClick={() => handleSelectSeries(series)}
                                                className="pill-button"
                                                style={{ background: 'var(--accent-2)', color: 'white' }}
                                            >
                                                {isAlreadyInLibrary ? 'Re-apply Series' : 'Select this Series'}
                                                <ChevronRight size={16} className="ml-1" />
                                            </Button>
                                        ) : null}

                                        {!canCurrentlySelect && !isAlreadyInLibrary && (
                                            <Button disabled className="pill-button opacity-60 cursor-not-allowed" style={{ background: 'var(--bg-tertiary)', color: 'var(--text-secondary)' }}>
                                                <Lock size={16} className="mr-2" />
                                                Locked
                                            </Button>
                                        )}
                                    </div>
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                                    {storiesToDisplay.map((story) => (
                                        <FolioStoryCard 
                                            key={story.id} 
                                            story={story} 
                                            isAdmin={isAdmin} 
                                            onEdit={handleEditStory}
                                            onGenerateImage={handleGenerateImage}
                                            isGenerating={regeneratingIds.includes(story.id)}
                                            isUnlocked={isAlreadyInLibrary || isAdmin}
                                            isGuest={isGuestMode}
                                        />
                                    ))}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            <AssignmentModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                series={selectedSeries}
                onAssign={handleAssign}
                isAssigning={isAssigning}
                token={token}
                platformTier={platformTier}
            />

            <FolioStoryModal
                isOpen={isCreateModalOpen}
                onClose={() => setIsCreateModalOpen(false)}
                token={token}
                storyToEdit={editingStory}
                existingSeries={seriesList}
                onSuccess={fetchFolioData}
            />

            <SeriesEditorModal
                isOpen={isSeriesEditorOpen}
                onClose={() => setIsSeriesEditorOpen(false)}
                token={token}
                seriesToEdit={editingSeries}
                onSuccess={fetchFolioData}
            />
        </div>
    );
};

export default FolioPage;